import { frozenThemeTokens, type ThemeToken } from "../../../../../../shared/frontend/theme/themeTokens.generated";
import React from "react";
import { useAuth } from "../auth/useAuth";
import { desktopPlatform, isM1DesktopShell, notifyDesktopTheme, subscribeDesktopThemeMode } from "../desktop/m1Desktop";
import { memberDefaultThemeId, readLegacyMemberThemeId, widgetShape, type ThemeMode } from "./theme";
import { ThemeProvider as SharedThemeProvider } from "../../../../../../shared/frontend/theme/ThemeProvider";
import { useAppTheme, useThemeAvailabilitySync } from "../../../../../../shared/frontend/theme/useAppTheme";
import { siblingThemeId, type ThemeDefinition, type ThemeFieldVariant } from "../../../../../../shared/frontend/theme/themeCatalog";

// process.platform-Werte der Desktop-Hülle.
const DESKTOP_PLATFORM_WINDOWS = "win32";
const DESKTOP_PLATFORM_MACOS = "darwin";
const DESKTOP_PLATFORM_LINUX = "linux";

function isPublicWidgetRouteFromHash(hash: string): boolean {
  // HashRouter: URLs look like "/#/book/abc" => window.location.hash = "#/book/abc"
  const h = (hash || "").trim();
  if (!h.startsWith("#/")) return false;
  const path = h.slice(1); // "/book/abc"
  return path.startsWith("/book/") || path.startsWith("/lead/") || path.startsWith("/contract-widget/");
}

type ThemeContextValue = {
  /** Wirksames Design aus dem gemeinsamen Katalog. */
  theme: ThemeDefinition;
  /** Design-Tokens: öffentliche Widgets haben ihren eigenen festen Satz (Antd-Grundwerte, neutrale Markenfarbe). */
  token: ThemeToken;
  /** Feldform des Designs; öffentliche Widgets behalten die Antd-Standardform. */
  fieldVariant?: ThemeFieldVariant;
  /** Kompatibilität mit Helligkeitsmeldungen älterer Desktop-Hüllen, soweit freigegeben. */
  setBrightness: (mode: ThemeMode) => void;
};

const ThemeContext = React.createContext<ThemeContextValue | null>(null);

/** Öffentliche Widgets: neutrales helles Design; ihr Erscheinungsbild kommt aus den Widget-Einstellungen. */
const widgetThemeId = "classic-light";

/** Zuletzt angemeldetes Konto des Geräts: Anmelde- und Tarifseiten zeigen dessen Theme statt des App-Standards. */
const lastAccountStorageKey = "m1member.theme.lastAccountId";

function readLastAccountId(): string | null {
  try {
    return localStorage.getItem(lastAccountStorageKey);
  } catch {
    return null;
  }
}

/**
 * m1member-Anbindung an die gemeinsame Theme-Laufzeit: Benutzerwahl je Konto und Gerät, Freigabe und
 * Standards vom Server, Desktop-Hülle und isolierte öffentliche Widgets.
 */
export function ThemeProvider(props: { children: React.ReactNode }) {
  const auth = useAuth();
  const accountId = auth.info?.accountId ?? null;
  const [isPublicWidgetRoute, setIsPublicWidgetRoute] = React.useState<boolean>(() =>
    typeof window !== "undefined" && isPublicWidgetRouteFromHash(window.location.hash));

  // Track hash route changes (HashRouter) to keep widget isolation stable.
  React.useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = () => setIsPublicWidgetRoute(isPublicWidgetRouteFromHash(window.location.hash));
    window.addEventListener("hashchange", handler);
    return () => window.removeEventListener("hashchange", handler);
  }, []);

  // Ohne Anmeldung gilt die Wahl des zuletzt angemeldeten Kontos; ohne ein solches der App-Standard.
  const themeAccountId = accountId ?? readLastAccountId();
  React.useEffect(() => {
    if (!accountId) return;
    try {
      localStorage.setItem(lastAccountStorageKey, accountId);
    } catch {
      // Ohne Speicher zeigt die Anmeldeseite den App-Standard.
    }
  }, [accountId]);

  const readLegacy = React.useCallback(() => readLegacyMemberThemeId(themeAccountId), [themeAccountId]);

  return (
    <SharedThemeProvider
      productDefaultThemeId={memberDefaultThemeId}
      storageKey={`m1member.theme.id.${themeAccountId ?? "anonymous"}`}
      readLegacyThemeId={readLegacy}
      forcedThemeId={isPublicWidgetRoute ? widgetThemeId : null}
    >
      <MemberThemeBridge isPublicWidgetRoute={isPublicWidgetRoute}>{props.children}</MemberThemeBridge>
    </SharedThemeProvider>
  );
}

function MemberThemeBridge({ isPublicWidgetRoute, children }: { isPublicWidgetRoute: boolean; children: React.ReactNode }) {
  const auth = useAuth();
  const shared = useAppTheme();
  const { theme, availability, setThemeId } = shared;

  useThemeAvailabilitySync(auth.api.getThemeAvailability.bind(auth.api), `${auth.info?.accountId ?? "anonymous"}:${auth.info?.tenantId ?? ""}`);

  // Merkmale der m1member-Oberfläche am Dokument (Desktop-Hülle, feste Rundung).
  React.useEffect(() => {
    if (typeof document === "undefined") return;
    const root = document.documentElement;
    root.dataset.shape = widgetShape;
    root.classList.toggle("mu-desktop-shell", isM1DesktopShell());
    const platform = desktopPlatform();
    root.classList.toggle("mu-desktop-platform-win32", platform === DESKTOP_PLATFORM_WINDOWS);
    root.classList.toggle("mu-desktop-platform-darwin", platform === DESKTOP_PLATFORM_MACOS);
    root.classList.toggle("mu-desktop-platform-linux", platform === DESKTOP_PLATFORM_LINUX);
  }, []);

  const setBrightness = React.useCallback((mode: ThemeMode) => {
    if (isPublicWidgetRoute || theme.brightness === mode) return;
    const sibling = siblingThemeId(theme.id, mode);
    if (sibling && (!availability || availability.availableThemeIds.includes(sibling))) setThemeId(sibling);
  }, [availability, isPublicWidgetRoute, setThemeId, theme]);

  // Ältere Desktop-Hüllen können noch Helligkeitsmeldungen senden; neue wählen das Theme im Kontomenü.
  React.useEffect(() => subscribeDesktopThemeMode(setBrightness), [setBrightness]);

  React.useEffect(() => {
    notifyDesktopTheme(theme);
  }, [theme]);

  const value = React.useMemo<ThemeContextValue>(() => ({
    theme,
    token: isPublicWidgetRoute ? frozenThemeTokens.widget : shared.token,
    fieldVariant: isPublicWidgetRoute ? undefined : shared.fieldVariant,
    setBrightness,
  }), [isPublicWidgetRoute, setBrightness, shared.token, shared.fieldVariant, theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = React.useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme muss innerhalb von ThemeProvider verwendet werden.");
  return ctx;
}
